import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Pencil, Tags, UtensilsCrossed } from 'lucide-react';
import { api, photoUrl } from '@/lib/api';

export default function AdminList() {
  const queryClient = useQueryClient();
  const recipesQuery = useQuery({
    queryKey: ['admin', 'recipes'],
    queryFn: api.adminListRecipes,
  });

  const deleteMutation = useMutation({
    mutationFn: api.adminDeleteRecipe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to="/"
            aria-label="Zpět na web"
            className="rounded-full p-2 text-text-muted hover:bg-accent-soft hover:text-text"
          >
            <ArrowLeft size={18} />
          </Link>
          <h1 className="truncate font-serif text-3xl tracking-tight">Recepty</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/categories"
            aria-label="Kategorie"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-text-muted transition-colors hover:border-accent/40 hover:text-text"
          >
            <Tags size={18} />
          </Link>
          <Link
            to="/admin/new"
            aria-label="Nový recept"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent text-white shadow-card transition-colors hover:bg-accent/90"
          >
            <Plus size={18} />
          </Link>
        </div>
      </div>

      {recipesQuery.isPending && <p className="text-sm text-text-muted">Načítám…</p>}

      {recipesQuery.data && recipesQuery.data.length === 0 && (
        <p className="rounded-2xl border border-dashed border-border bg-surface p-6 text-center text-text-muted">
          Zatím žádné recepty. Začni tlačítkem „Nový“.
        </p>
      )}

      {recipesQuery.data && recipesQuery.data.length > 0 && (
        <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-surface">
          {recipesQuery.data.map((r) => {
            const url = photoUrl(r.photo_key);
            return (
              <li key={r.id} className="flex items-center gap-4 px-4 py-3">
                <Link
                  to={`/recipe/${r.slug}`}
                  className="flex min-w-0 flex-1 items-center gap-4 rounded-lg transition-colors hover:opacity-90"
                >
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-accent-soft">
                    {url ? (
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-text-muted/40">
                        <UtensilsCrossed size={20} />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{r.title}</p>
                    <p className="truncate text-xs text-text-muted">/{r.slug}</p>
                  </div>
                </Link>
                <Link
                  to={`/admin/recipe/${r.id}`}
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm text-text-muted hover:bg-accent-soft hover:text-accent"
                >
                  <Pencil size={14} />
                  <span className="hidden sm:inline">Upravit</span>
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Smazat „${r.title}"?`)) deleteMutation.mutate(r.id);
                  }}
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm text-text-muted hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
